'use client';

import { c as _c } from "react/compiler-runtime";
import type { HeroState } from './utils';
import type { ValueWithTrendTemplateCard } from '@effektify/engine';
import type React from 'react';
import { WithIcon as TrendWithIcon } from '@effektify/components/Trend';
import { formatValue } from '@effektify/components/utils';
import { useCardState, useValueWithTrendProps } from '@effektify/engine';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import { motion } from 'motion/react';
import { FlexColumn, FlexRow } from 'src/components/Atoms/FlexBox';
import { HERO_MOTION } from './motion';
import StatusDot from './StatusDot';
import { toNumber } from './utils';
type FormatType = Parameters<typeof formatValue>[1]['type'];
interface HeroKpiStatProps {
  card: ValueWithTrendTemplateCard;
  index: number;
  heroState: HeroState;
  isInView: boolean;
  isDataSettled: boolean;
}
const HeroKpiStat: React.FC<HeroKpiStatProps> = t0 => {
  const $ = _c(46);
  const {
    card,
    index,
    heroState: pageState,
    isInView,
    isDataSettled
  } = t0;
  const {
    data,
    shouldRender
  } = useCardState(card);
  const {
    title,
    original,
    comparison,
    noComparison
  } = useValueWithTrendProps(card, data);
  let T0;
  let T1;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== comparison || $[1] !== index || $[2] !== isDataSettled || $[3] !== isInView || $[4] !== noComparison || $[5] !== original || $[6] !== pageState || $[7] !== shouldRender || $[8] !== title?.text) {
    t9 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const value = toNumber(original?.value);
      const heroState = pageState === "normal" && value === null && !isDataSettled ? "loading" : pageState;
      if (!shouldRender) {
        t9 = null;
        break bb0;
      }
      const {
        y,
        duration,
        delay,
        stagger
      } = HERO_MOTION.kpi;
      const label = title?.text ?? "";
      T1 = motion.div;
      if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
        t6 = {
          opacity: 0,
          y
        };
        $[20] = t6;
      } else {
        t6 = $[20];
      }
      if ($[21] !== isInView) {
        t7 = isInView ? {
          opacity: 1,
          y: 0
        } : undefined;
        $[21] = isInView;
        $[22] = t7;
      } else {
        t7 = $[22];
      }
      const t10 = delay + index * stagger;
      if ($[23] !== t10) {
        t8 = {
          duration,
          delay: t10
        };
        $[23] = t10;
        $[24] = t8;
      } else {
        t8 = $[24];
      }
      T0 = FlexColumn;
      t1 = _temp;
      if ($[25] !== heroState) {
        t2 = heroState === "loading" && <><Skeleton variant="text" width={112} height={36} /><Skeleton variant="text" width={80} /><Skeleton variant="text" width={56} /></>;
        $[25] = heroState;
        $[26] = t2;
      } else {
        t2 = $[26];
      }
      if ($[27] !== heroState || $[28] !== label) {
        t3 = heroState === "no-connections" && <><Skeleton variant="rounded" animation={false} width={112} height={32} /><Typography variant="body-sm" sx={_temp2}>{label}</Typography><Typography variant="body-xs" sx={_temp3}>Ready for data</Typography></>;
        $[27] = heroState;
        $[28] = label;
        $[29] = t3;
      } else {
        t3 = $[29];
      }
      if ($[30] !== heroState || $[31] !== label) {
        t4 = heroState === "no-data" && <><Skeleton variant="text" width={112} height={36} /><Typography variant="body-sm" sx={_temp4}>{label}</Typography><FlexRow sx={_temp5}><StatusDot tone="waiting" pulsing={true} /><Typography variant="body-xs" sx={_temp6}>Waiting for data</Typography></FlexRow></>;
        $[30] = heroState;
        $[31] = label;
        $[32] = t4;
      } else {
        t4 = $[32];
      }
      t5 = heroState === "normal" && <><Typography variant="number-xl" sx={_temp7}>{value !== null && original ? formatValue(value, {
            type: original.type as FormatType,
            currency: original.currency,
            precision: 0,
            short: original.short !== false
          }) : "\u2014"}</Typography><Typography variant="body-sm" sx={_temp8}>{label}</Typography>{!noComparison && comparison && <TrendWithIcon data={comparison} isLoading={false} float="left" />}</>;
    }
    $[0] = comparison;
    $[1] = index;
    $[2] = isDataSettled;
    $[3] = isInView;
    $[4] = noComparison;
    $[5] = original;
    $[6] = pageState;
    $[7] = shouldRender;
    $[8] = title?.text;
    $[9] = T0;
    $[10] = T1;
    $[11] = t1;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    T0 = $[9];
    T1 = $[10];
    t1 = $[11];
    t2 = $[12];
    t3 = $[13];
    t4 = $[14];
    t5 = $[15];
    t6 = $[16];
    t7 = $[17];
    t8 = $[18];
    t9 = $[19];
  }
  if (t9 !== Symbol.for("react.early_return_sentinel")) {
    return t9;
  }
  let t10;
  if ($[33] !== T0 || $[34] !== t1 || $[35] !== t2 || $[36] !== t3 || $[37] !== t4 || $[38] !== t5) {
    t10 = <T0 sx={t1}>{t2}{t3}{t4}{t5}</T0>;
    $[33] = T0;
    $[34] = t1;
    $[35] = t2;
    $[36] = t3;
    $[37] = t4;
    $[38] = t5;
    $[39] = t10;
  } else {
    t10 = $[39];
  }
  let t11;
  if ($[40] !== T1 || $[41] !== t10 || $[42] !== t6 || $[43] !== t7 || $[44] !== t8) {
    t11 = <T1 initial={t6} animate={t7} transition={t8}>{t10}</T1>;
    $[40] = T1;
    $[41] = t10;
    $[42] = t6;
    $[43] = t7;
    $[44] = t8;
    $[45] = t11;
  } else {
    t11 = $[45];
  }
  return t11;
};
export default HeroKpiStat;
function _temp(theme) {
  return {
    gap: theme.sizes.spacing["02"],
    minWidth: "7rem"
  };
}
function _temp2(theme_0) {
  return {
    color: theme_0.colors.text.secondary
  };
}
function _temp3(theme_1) {
  return {
    color: theme_1.colors.text.tertiary
  };
}
function _temp4(theme_2) {
  return {
    color: theme_2.colors.text.tertiary
  };
}
function _temp5(theme_3) {
  return {
    alignItems: "center",
    gap: theme_3.sizes.spacing["03"]
  };
}
function _temp6(theme_4) {
  return {
    color: theme_4.colors.text.tertiary
  };
}
function _temp7(theme_5) {
  return {
    color: theme_5.colors.text.primary,
    textAlign: "left"
  };
}
function _temp8(theme_6) {
  return {
    color: theme_6.colors.text.tertiary
  };
}